import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import moment from 'moment';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Subtitle,
} from '../../shared';
import { showFeedback } from '../../../store/ui/ui.actions';
import { sendSingleRequest } from '../../../apis';
import { numberToMoneyStr } from '../../../entities/util-functions';
import { IXeroInvoice } from '../../../entities/subscription.entities';
import GrowerInvoiceCreateView from '../../budget/GrowerInvoiceCreateView';
import {
  loadFarmsData,
  loadQueueData,
  loadSpatStorages,
} from '../../../store/farms/farms.actions';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import {
  displayValueUnit,
  numToStr,
  toMillisecond,
  toSecond,
} from '../../../lib/common.helpers';
import { IMusselHarvest } from '../../../entities/growing.entities';
import { IMusselLineResource } from '../../../entities/farms.entities';
import ExpensesForm from '../../grower-views/ExpensesForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import { SelectFarmsByFarmID } from '../../../store/extra/extra.selector';
import { divideEqually } from '../../../lib/common.helpers';
import MoreFieldsForm from '../HarvestCompleteModal/MoreFieldsForm';
import HarvestBatchSelect from '../HarvestCompleteModal/HarvestBatchSelect';
import { selectAccount } from '../../../store/auth/auth.selector';

interface IFormData {
  seeding_id?: number;
  rope_bags_quantity: number | null;
  harvest_number: string | null;
  complete_date: number;
  port_of_unload: string | null;
  shell_length: number | null;
  shell_length_max: number | null;
  company: string | null;
  comment: string | null;
}

const defaultForm: IFormData = {
  complete_date: moment().toDate().getTime(),
  shell_length: null,
  shell_length_max: null,
  company: null,
  harvest_number: null,
  port_of_unload: null,
  rope_bags_quantity: null,
  comment: null,
};

const extractData = (data: any) => {
  if (!data) return { ...defaultForm };
  let res: any = { ...defaultForm };
  for (let k in defaultForm) {
    if (!data[k]) continue;
    res[k] = k === 'complete_date' ? 1000 * data[k] : data[k];
  }
  return res;
};

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface IHarvestLine {
  line_id: number;
  bags_quantity: number | null;
  is_full: boolean;
  line_length: string | null;
  harvest_groups?: IHarvestGroup[];
}

interface Props {
  visible: boolean;
  lineData?: IMusselLineResource;
  onClose: (d: any) => void;
  onConfirm: (d: any) => void;
  harvestData?: IMusselHarvest;
  updateId?: number;
  paramData?: any;
  overConfirm?: (d: any) => void;
  confirmID?: number;
  className?: string;
  warningMessage?: string;
}

const MusselGrowerHarvestModal = ({
  visible,
  lineData,
  onConfirm,
  onClose,
  harvestData,
  updateId,
  overConfirm,
  paramData,
  confirmID,
  className,
  warningMessage,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const farms = SelectFarmsByFarmID(lineData?.farm_id);
  const unitType = useSelector(selectAccount)?.display_unit;
  const allLinesSrc = farms.flatMap(
    f => f.lines.filter(x => x.crop_type === 'MUSSEL') as IMusselLineResource[],
  );
  const allLines =
    allLinesSrc.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.farm_name} - ${x.line_name}`,
      isGrowing: !!x.growing_cycle,
      last_harvest: x.last_harvest,
    })) ?? [];
  const linesOption = updateId ? allLines : allLines.filter(x => x.isGrowing);

  const [disabled, setDisabled] = useState(false);
  const [harvestedLines, setHarvestedLines] = useState<IHarvestLine[]>(
    lineData
      ? [
          {
            line_id: lineData.id,
            bags_quantity: harvestData?.bags_quantity ?? null,
            is_full: harvestData?.is_full ?? true,
            line_length: harvestData?.line_length?.toString() ?? null,
            harvest_groups: harvestData?.harvest_groups,
          },
        ]
      : [],
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [submittedData, setSubmittedData] = useState<any>();
  const [harvestCosts, setHarvestCosts] = useState<IExpenseRequest[]>(
    harvestData?.expense_items?.map(x => ({
      name: x.name,
      amount: x.amount,
      quantity: x.quantity,
      unit_price: x.quantity
        ? Math.round((x.amount / x.quantity) * 100) / 100
        : null,
    })) ?? [{ name: '', amount: null, quantity: null }],
  );
  const [formData, setFormData] = useState<IFormData>(extractData(harvestData));
  const [totAmount, setTotAmount] = useState(numToStr(harvestData?.amount));
  const [totIncome, setTotIncome] = useState(numToStr(harvestData?.income));
  const [isMiddle, setIsMiddle] = useState(
    harvestData && !harvestData.is_final ? true : false,
  );

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message,
        duration: -1,
      }),
    );

  const updateHarvestLine = (id: number, key: keyof IHarvestLine, val: any) => {
    let tmp = [...harvestedLines];
    const i = tmp.findIndex(x => x.line_id === id);
    if (key === 'bags_quantity') val = Number(val);
    if (i > -1) (tmp[i] as any)[key] = val;
    setHarvestedLines(tmp);
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (key === 'shell_length' || key === 'shell_length_max') {
      value = value.length <= 0 ? null : Number(value);
    }
    setFormData(prv => ({ ...prv, [key]: value }));
  };
  const selectLine = (val: string[]) => {
    let tmp = val.map(x => ({
      line_id: Number(x),
      bags_quantity: null,
      is_full: true,
      line_length: null,
    }));
    for (let t of harvestedLines) {
      const i = tmp.findIndex(x => x.line_id === t.line_id);
      if (i > -1) {
        tmp[i].bags_quantity = t.bags_quantity as any;
        tmp[i].is_full = t.is_full;
        tmp[i].line_length = t.line_length as any;
      }
    }
    setHarvestedLines(tmp);
  };
  const validData = () => {
    if (harvestedLines.length <= 0) {
      showError(translate(lang, '_sel_harvested_line'));
      return null;
    }
    if (
      harvestedLines.findIndex(
        x => !x.bags_quantity || Number(x.bags_quantity) <= 0,
      ) > -1
    ) {
      showError(translate(lang, '_input_valid_num_bags'));
      return null;
    }
    if (Number(totAmount) <= 0) {
      showError(translate(lang, '_input_valid_harvest_amt'));
      return null;
    }
    const totBags = harvestedLines.reduce(
      (p, c) => p + Number(c.bags_quantity),
      0,
    );
    if (updateId) {
      if (harvestedLines.length !== 1) {
        showError(
          translate(
            lang,
            '_unknown_err_contact_developer',
          ),
        );
        return null;
      }
      const tmpL = allLines.find(x => Number(x.id) === lineData?.id);
      if (!tmpL) {
        showError(
          translate(
            lang,
            '_data_loading_try_after',
          ),
        );
        return null;
      }
      if (
        harvestData?.is_full !== undefined &&
        harvestData.is_full !== harvestedLines[0].is_full &&
        tmpL.last_harvest?.id !== updateId
      ) {
        showError(translate(lang, '_impossible_harvest_update'));
        return null;
      }

      return {
        ...formData,
        is_final: !isMiddle,
        line_length: harvestedLines[0].line_length,
        is_full: harvestedLines[0].is_full,
        bags_quantity: harvestedLines[0].bags_quantity,
        harvest_groups: harvestedLines[0].harvest_groups?.map((x, i) => ({
          batch_seed_id: x.batch_seed_id,
          amount: divideEqually(
            Number(totAmount),
            harvestedLines[0].harvest_groups?.length ?? 0,
            i,
          ),
          line_length: divideEqually(
            Number(harvestedLines[0].line_length),
            harvestedLines[0].harvest_groups?.length ?? 0,
            i,
          ),
        })),
        amount: Number(totAmount),
        income: Number(totIncome),
        expenses: harvestCosts.filter(x => x.quantity && x.amount),
        complete_date: toSecond(formData.complete_date),
      };
    } else {
      return {
        ...formData,
        lines: harvestedLines.map(x => {
          const lAmt = (Number(totAmount) / totBags) * Number(x.bags_quantity);
          return {
            ...x,
            is_final: !isMiddle,
            amount: lAmt,
            income: (Number(totIncome) / totBags) * Number(x.bags_quantity),
            harvest_groups: x.harvest_groups?.map((y, i) => ({
              batch_seed_id: y.batch_seed_id,
              amount: divideEqually(lAmt, x.harvest_groups?.length ?? 0, i),
              line_length: divideEqually(
                Number(x.line_length),
                x.harvest_groups?.length ?? 0,
                i,
              ),
            })),
          };
        }),
        expenses: harvestCosts.filter(x => x.quantity && x.amount),
        complete_date: toSecond(formData.complete_date),
      };
    }
  };
  const invoiceDetail = (): IXeroInvoice => {
    const totBags = harvestedLines.reduce(
      (p, c) => p + Number(c.bags_quantity),
      0,
    );
    let line_items: any = [];
    for (let hl of harvestedLines) {
      const line = allLines.find(t => Number(t.id) === hl.line_id);
      if (!line) continue;
      const rate = Number(hl.bags_quantity) / totBags;
      line_items.push({
        item_name: line.label,
        description: 'Income',
        quantity: 1,
        unit_price: Number(totIncome) * rate,
      });
      for (let item of harvestCosts) {
        const qty = (Number(item.quantity) ? Number(item.quantity) : 1) * rate;
        const amt = Number(item.amount) ? Number(item.amount) * rate : 0;
        line_items.push({
          item_name: line.label,
          description: item.name,
          quantity: qty,
          unit_price: -amt / qty,
        });
      }
    }
    return {
      reference: 'Harvest Invoice',
      contact_id: '',
      line_items,
      type: 'ACCREC',
    };
  };
  const confirmClick = async () => {
    const data = validData();
    if (!data) return;

    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }
    setDisabled(true);
    if (overConfirm) {
      overConfirm(data);
      return;
    }
    const res = updateId
      ? await sendSingleRequest(
          data,
          'POST',
          `api/farm/line/harvest-update/${updateId}`,
          true,
        )
      : confirmID
        ? await sendSingleRequest(
            { ...data, id: confirmID },
            'POST',
            'api/queue/confirm-bulk-harvest/mussel',
            true,
          )
        : await sendSingleRequest(
            data,
            'POST',
            'api/farm/line/bulk-mussel-harvest',
            true,
          );
    if (res.status) {
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessageModal: true,
          message: translate(
            lang,
            updateId
              ? 'Updated successfully'
              : (res.data?.message ?? '_harvested_ok'),
          ),
          type: 'success',
        }),
      );
      setSubmittedData(res.data);
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      if (isMiddle) {
        dispatch(loadSpatStorages());
      }
    } else {
      setDisabled(false);
      showError(translate(lang, res.data?.message ?? 'Failed to harvest'));
    }
  };

  useEffect(() => {
    if (paramData) {
      const {
        lines,
        bags_quantity,
        is_full,
        is_final,
        line_length,
        expenses,
        complete_date,
        amount,
        income,
        ...params
      } = paramData;

      if (lines) {
        setIsMiddle(!lines[0].is_final);
        setHarvestedLines(
          lines.map((x: any) => ({
            line_id: x.line_id,
            bags_quantity: x.bags_quantity,
            is_full: x.is_full,
            line_length: x.line_length,
            harvest_groups: x.harvest_groups,
          })),
        );
        setTotAmount(lines.reduce((p: number, c: any) => p + c.amount, 0));
        setTotIncome(lines.reduce((p: number, c: any) => p + c.income, 0));
      } else if (lineData) {
        setIsMiddle(!is_final);
        setHarvestedLines([
          {
            line_id: lineData.id,
            bags_quantity: bags_quantity,
            is_full: is_full,
            line_length: line_length,
          },
        ]);
        if (amount) setTotAmount(amount);
        if (income) setTotIncome(income);
      }
      if (expenses) {
        setHarvestCosts(expenses);
      }
      setFormData({ ...params, complete_date: toMillisecond(complete_date) });
    }
  }, [paramData, lineData]);

  const costSum = harvestCosts.reduce((p, c) => p + Number(c.amount), 0);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className={className}
      closable
      maskClosable={false}
      closeIcon={<CloseIcon />}
      width={1000}
      footer={null}
    >
      {!showConfirm ? (
        <div className='section-modal wrap'>
          <div className='d-flex align-center mb-17'>
            <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
              {translate(lang, 'INVOICE DETAILS')}
            </Subtitle>
          </div>
          {!!warningMessage && (
            <div className='header-alert'>{warningMessage}</div>
          )}
          <div className='section'>
            <div className='mb-17'>
              <Dropdown
                label={translate(lang, '_sel_harvested_lines')}
                placeholder={translate(lang, '_sel_harvested_lines')}
                mode='multiple'
                options={linesOption}
                value={harvestedLines.map(x => x.line_id.toString()) as any}
                onChange={(v: any) => selectLine(v)}
                showSearch={true}
                disabled={!!updateId}
              />
            </div>
            <div className='mb-17'>
              {harvestedLines.map(line => {
                const lineSrc = allLinesSrc.find(x => x.id === line.line_id);
                return (
                  <div className='mb-15 d-flex align-center' key={line.line_id}>
                    <div className='mr-7' style={{ width: 190 }}>
                      <Input
                        type='number'
                        label={
                          linesOption.find(x => Number(x.id) === line.line_id)
                            ?.label ?? ''
                        }
                        placeholder={translate(lang, 'Bags quantity')}
                        value={numToStr(line.bags_quantity)}
                        onChange={e =>
                          updateHarvestLine(
                            line.line_id,
                            'bags_quantity',
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className='ml-7 mr-7' style={{ width: 120 }}>
                      <Dropdown
                        label='F / P'
                        value={!!line.is_full ? 'full' : 'partial'}
                        options={[
                          {
                            id: 'full',
                            value: 'full',
                            label: translate(lang, 'Full'),
                          },
                          {
                            id: 'partial',
                            value: 'partial',
                            label: translate(lang, 'Partial'),
                          },
                        ]}
                        onChange={v =>
                          updateHarvestLine(
                            line.line_id,
                            'is_full',
                            v === 'full',
                          )
                        }
                      />
                    </div>
                    <div className='ml-7 mr-7' style={{ width: 190 }}>
                      <Input
                        type='number'
                        label={translate(lang, 'Harvested length')}
                        value={line.line_length ?? ''}
                        onChange={e =>
                          updateHarvestLine(
                            line.line_id,
                            'line_length',
                            e.target.value,
                          )
                        }
                        unit='m'
                      />
                    </div>
                    {!line.is_full &&
                      (lineSrc?.growing_cycle?.seedings.length ?? 0) > 0 && (
                        <div className='ml-7' style={{ flex: 1 }}>
                          <HarvestBatchSelect
                            type='MUSSEL'
                            growingSeeds={
                              lineSrc?.growing_cycle?.seedings ?? []
                            }
                            values={
                              line.harvest_groups?.map(x => x.batch_seed_id) ??
                              []
                            }
                            onChange={sIds =>
                              updateHarvestLine(
                                line.line_id,
                                'harvest_groups',
                                sIds
                                  ? sIds.map(id => ({ batch_seed_id: id }))
                                  : undefined,
                              )
                            }
                          />
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className='section'>
            <div className='mb-17'>
              <CheckboxButton
                label={translate(lang, '_not_final')}
                checked={isMiddle}
                onChange={e => setIsMiddle(e.target.checked)}
              />
            </div>
            <div className='item-row mb-17'>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Harvest Amount')}
                  type='number'
                  onChange={e => setTotAmount(e.target.value)}
                  value={totAmount}
                  unit='kg'
                />
              </div>
              <div className='item-cell'>
                {!isMiddle && (
                  <Input
                    label={translate(lang, 'Harvest Income')}
                    type='number'
                    onChange={e => setTotIncome(e.target.value)}
                    value={totIncome}
                    unit='$'
                  />
                )}
              </div>
              <div className='item-cell'>
                <Datepicker
                  label={translate(lang, '_harvest_complete_date')}
                  defaultValue={formData.complete_date}
                  onChange={e =>
                    e && updateForm('complete_date', e.toDate().getTime())
                  }
                />
              </div>
            </div>

            <div className='item-row mb-17'>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Shell length min')}
                  type='number'
                  onChange={e => updateForm('shell_length', e.target.value)}
                  value={numToStr(formData.shell_length)}
                  unit='mm'
                />
              </div>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Shell length max')}
                  type='number'
                  onChange={e => updateForm('shell_length_max', e.target.value)}
                  value={numToStr(formData.shell_length_max)}
                  unit='mm'
                />
              </div>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Harvest number')}
                  placeholder={translate(lang, 'Harvest number')}
                  type='text'
                  onChange={e => updateForm('harvest_number', e.target.value)}
                  value={formData.harvest_number ?? ''}
                />
              </div>
            </div>
            <ExpensesForm
              title={translate(lang, 'Harvest Expenses')}
              data={harvestCosts}
              setData={setHarvestCosts}
              hideTotal={true}
            />
            <div className='item-row mt-7 strong'>
              <div className='item-cell'>{translate(lang, 'Totals')}</div>
              <div className='item-cell'>
                {displayValueUnit(Number(totAmount), unitType, 'weight_kg')}
              </div>
              <div className='item-cell'>
                <div>{numberToMoneyStr(Number(totIncome))} ($)</div>
                <div>-{numberToMoneyStr(costSum)} ($)</div>
                <div>{numberToMoneyStr(Number(totIncome) - costSum)} ($)</div>
              </div>
              <div className='item-cell-half'></div>
            </div>
          </div>
          <div className='section'>
            <MoreFieldsForm
              type='MUSSEL'
              farmID={lineData?.farm_id}
              form={formData}
              onChange={d => setFormData(d)}
            />
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Comment')}
                placeholder={translate(lang, 'Comment')}
                type='textarea'
                onChange={e => updateForm('comment', e.target.value)}
                value={formData.comment ?? ''}
                name='comment'
              />
            </div>
          </div>
          <div className='modal-button d-flex justify-end align-center'>
            <Button
              width='small'
              className='rsp-btn'
              size={2}
              type='fill'
              color='red'
              onClick={onClose}
              disabled={disabled}
            >
              {translate(lang, 'Cancel')}
            </Button>
            <Button
              width='small'
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={confirmClick}
              disabled={disabled}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='section-modal wrap'>
          <GrowerInvoiceCreateView
            farmID={lineData?.farm_id}
            invoice={invoiceDetail()}
            onClose={() => onConfirm(submittedData)}
            showButton={!!submittedData}
          />
          {!submittedData && (
            <div className='modal-button d-flex justify-end align-center'>
              <Button
                width='small'
                size={2}
                type='fill'
                color='red'
                className='rsp-btn'
                onClick={() => setShowConfirm(false)}
                disabled={disabled}
              >
                {translate(lang, 'Go Back')}
              </Button>
              <Button
                width='small'
                size={2}
                type='fill'
                color='green'
                className='rsp-btn ml-16'
                onClick={confirmClick}
                disabled={disabled}
              >
                {translate(lang, 'Confirm')}
              </Button>
            </div>
          )}
        </div>
      )}
      <ModalFeedbackView />
    </Modal>
  );
};

export default MusselGrowerHarvestModal;
